import { useMemo, useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Text,
  VStack,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Alert,
  AlertIcon,
  Spinner,
  Center,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useZones } from '../../hooks/useZones';

const buildSnippet = ({ baseUrl, adId, zoneId, campaignId, width, height }) => {
  const qs = `ad=${adId}&zone=${zoneId}&c=${campaignId}&type=email`;
  return `<!-- StatFalcon Email Ad -->
<a href="${baseUrl}/api/ads/click?${qs}">
  <img src="${baseUrl}/api/ads/image/${adId}?zone=${zoneId}&c=${campaignId}&type=email"
       alt="Advertisement" width="${width}" height="${height}"
       style="border:0;display:block;max-width:100%;height:auto;" />
</a>`;
};

export const EmailSnippetModal = ({ isOpen, onClose, ad }) => {
  const toast = useToast();
  const { data: zones, loading } = useZones();
  const codeBg = useColorModeValue('gray.50', 'gray.900');
  const codeBorder = useColorModeValue('gray.200', 'gray.700');

  const baseUrl = useMemo(
    () => window.location.origin.replace(':3058', ':3059'),
    []
  );

  const emailZones = useMemo(
    () => (zones || []).filter((z) => z.zone_type === 'email'),
    [zones]
  );

  const [zoneId, setZoneId] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setZoneId('');
      setCopied(false);
    }
  }, [isOpen]);

  const selectedZone = useMemo(
    () => emailZones.find((z) => String(z.zone_id) === String(zoneId)) || null,
    [emailZones, zoneId]
  );

  const dimensionsMatch =
    !selectedZone || !ad
      ? true
      : Number(selectedZone.width) === Number(ad.width) &&
        Number(selectedZone.height) === Number(ad.height);

  const snippet = useMemo(() => {
    if (!ad || !selectedZone) return '';
    return buildSnippet({
      baseUrl,
      adId: ad.ad_id || ad.id,
      zoneId: selectedZone.zone_id,
      campaignId: ad.campaign_id,
      width: ad.width,
      height: ad.height,
    });
  }, [ad, selectedZone, baseUrl]);

  const handleCopy = () => {
    if (!snippet) return;
    navigator.clipboard.writeText(snippet);
    setCopied(true);
    toast({ title: 'Snippet copied', status: 'success', duration: 2000 });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" mx={4}>
        <ModalHeader>Email Snippet</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {ad && (
              <Text fontSize="sm" color="gray.500">
                Ad: <Text as="span" fontWeight="600" color="inherit">{ad.name}</Text>{' '}
                · {ad.width}×{ad.height}
              </Text>
            )}

            {loading ? (
              <Center py={6}>
                <Spinner color="purple.500" />
              </Center>
            ) : emailZones.length === 0 ? (
              <Alert status="warning" borderRadius="lg">
                <AlertIcon />
                No email zones found. Create a zone with type "email" first.
              </Alert>
            ) : (
              <FormControl>
                <FormLabel>Email zone</FormLabel>
                <Select
                  placeholder="Select an email zone"
                  value={zoneId}
                  onChange={(e) => setZoneId(e.target.value)}
                >
                  {emailZones.map((z) => (
                    <option key={z.zone_id} value={z.zone_id}>
                      {z.zone_name} ({z.width}×{z.height})
                      {z.site_name ? ` — ${z.site_name}` : ''}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedZone && !dimensionsMatch && (
              <Alert status="warning" borderRadius="lg">
                <AlertIcon />
                Ad dimensions ({ad.width}×{ad.height}) don't match zone
                dimensions ({selectedZone.width}×{selectedZone.height}). The
                image will still be served, but rendering may stretch.
              </Alert>
            )}

            {snippet && (
              <>
                <FormControl>
                  <FormLabel>Paste into your HTML newsletter</FormLabel>
                  <Textarea
                    value={snippet}
                    readOnly
                    fontFamily="mono"
                    fontSize="xs"
                    rows={8}
                    bg={codeBg}
                    borderColor={codeBorder}
                    onFocus={(e) => e.target.select()}
                  />
                </FormControl>

                <Box fontSize="xs" color="gray.500">
                  <Text>
                    Each open hits <Text as="span" fontFamily="mono">/api/ads/image</Text>{' '}
                    and records an impression; clicks redirect through{' '}
                    <Text as="span" fontFamily="mono">/api/ads/click</Text>. For
                    per-issue breakdown, create a separate campaign per
                    newsletter issue and attach the ad to it.
                  </Text>
                </Box>
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} borderRadius="lg">
            Close
          </Button>
          <Button
            colorScheme="purple"
            onClick={handleCopy}
            isDisabled={!snippet}
            leftIcon={<FontAwesomeIcon icon={copied ? faCheck : faCopy} />}
            borderRadius="lg"
          >
            {copied ? 'Copied' : 'Copy snippet'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EmailSnippetModal;
